// @ts-check

/** Ensures `window.StoreSelectionState` exists before this module body runs (cards may load before scripts.liquid). */
import '@theme/store-selection-state';

import { ThemeEvents } from '@theme/events';

/** Shopify per-location inventory quantity ceiling — used as synthetic stock when pickup is Tomorrow (MF20 bypass). */
const SHOPIFY_MAX_INVENTORY_QUANTITY = 1000000000;

/**
 * MF36 inventory-enforcement window (local bakery time). The window decides
 * what happens to a bakery that has NOT entered inventory today: inside it that
 * bakery is sold out, outside it the product is purchasable with no hard limit.
 * A bakery that HAS entered inventory (MF36 `bakeryStockSynced` true) has its
 * MF20 stock enforced at any hour, so the window never applies to it.
 *
 * The bounds are merchant-managed in the MO08 `mo08_inventory_window`
 * metaobject and stamped onto `<html>` by layout/theme.liquid. These constants
 * are the fallback: the metaobject definition and its single entry are created
 * per store by hand (neither is carried by `shopify theme push`), so a store
 * without them must keep working on the original spec values.
 */
const INVENTORY_WINDOW_START_MINUTES = 11 * 60; // 11:00am
const INVENTORY_WINDOW_END_MINUTES = 21 * 60; // 9:00pm (inclusive)

/** @type {{ start: number, end: number } | null} */
let inventoryWindowMinutes = null;

/**
 * Parses a 24-hour `HH:MM` wall-clock string into minutes past midnight.
 *
 * @param {string | undefined} raw
 * @returns {number | null}
 */
function parseWindowBound(raw) {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(raw ?? '').trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * Window bounds from MO08 (`data-inventory-window-start` / `-end` on `<html>`),
 * falling back to the spec defaults. Memoized — the values are baked into the
 * document at render.
 *
 * Both bounds must parse or neither is used, so a half-configured entry can't
 * pair a merchant value with a default and produce a window nobody chose. A
 * wrapped window (end before start) is left as-is for
 * `isInventoryWindowActive` to interpret as spanning midnight.
 *
 * @returns {{ start: number, end: number }}
 */
function getInventoryWindowMinutes() {
  if (inventoryWindowMinutes) return inventoryWindowMinutes;

  const data = typeof document !== 'undefined' ? document.documentElement?.dataset : undefined;
  const start = parseWindowBound(data?.inventoryWindowStart);
  const end = parseWindowBound(data?.inventoryWindowEnd);

  inventoryWindowMinutes =
    start != null && end != null
      ? { start, end }
      : { start: INVENTORY_WINDOW_START_MINUTES, end: INVENTORY_WINDOW_END_MINUTES };

  return inventoryWindowMinutes;
}

/**
 * @typedef {'available' | 'sold_out' | 'unavailable'} AvailabilityState
 *
 * @typedef {{ state: AvailabilityState, stock: number }} AvailabilityResult
 *
 * SSD estimated pickup: `today` applies MF20 for sold_out; `tomorrow` ignores MF20 when in MF16.
 *
 * @typedef {'today' | 'tomorrow'} AvailabilityPickupDay
 */

/**
 * Normalise the availability comparison key `B`: trim, and if a Shopify Location
 * GID was passed, use the last path segment (legacy). MF16/MF20 use internal
 * bakery ids from `custom.mf01_internal_bakery_id` — match via
 * `amb_store_selection.internalBakeryId`.
 *
 * @param {string | number | null | undefined} raw
 * @returns {string | null}
 */
function normaliseAvailabilityBakeryId(raw) {
  if (raw == null || raw === '') return null;
  let s = String(raw).trim();
  if (!s) return null;
  if (s.includes('/')) s = String(s.split('/').pop() ?? '').trim();
  return s || null;
}

/**
 * Resolves MF01 internal bakery id for MF16/MF20 matching from a selection
 * snapshot or from `window.StoreSelectionState.getSelection()`.
 *
 * @param {Record<string, unknown> | null | undefined} [selection]
 * @returns {string | null}
 */
export function getSelectedAvailabilityBakeryId(selection) {
  const stateApi =
    typeof window !== 'undefined' ? /** @type {any} */ (window).StoreSelectionState : undefined;
  const sel =
    selection != null && typeof selection === 'object'
      ? selection
      : stateApi && typeof stateApi.getSelection === 'function'
        ? stateApi.getSelection()
        : null;
  if (!sel || typeof sel !== 'object') return null;
  const ib = String(/** @type {any} */ (sel).internalBakeryId || '').trim();
  return ib || null;
}

/**
 * SSD estimated pickup day from `amb_store_selection.pickupDay` (`today` | `tomorrow`).
 * Anything other than explicit `tomorrow` is treated as **today** (MF20 applies).
 *
 * @param {Record<string, unknown> | null | undefined} [selection]
 * @returns {AvailabilityPickupDay}
 */
export function getSelectedPickupDay(selection) {
  const stateApi =
    typeof window !== 'undefined' ? /** @type {any} */ (window).StoreSelectionState : undefined;
  const sel =
    selection != null && typeof selection === 'object'
      ? selection
      : stateApi && typeof stateApi.getSelection === 'function'
        ? stateApi.getSelection()
        : null;
  const raw = String(/** @type {any} */ (sel)?.pickupDay || '').trim().toLowerCase();
  return raw === 'tomorrow' ? 'tomorrow' : 'today';
}

/**
 * Selected bakery's IANA timezone from `amb_store_selection.timezone` (written
 * by the SSD from the location's `amb.timezone` metafield). Null for stale
 * rows persisted before the field existed.
 *
 * @param {Record<string, unknown> | null | undefined} [selection]
 * @returns {string | null}
 */
export function getSelectedBakeryTimezone(selection) {
  const stateApi =
    typeof window !== 'undefined' ? /** @type {any} */ (window).StoreSelectionState : undefined;
  const sel =
    selection != null && typeof selection === 'object'
      ? selection
      : stateApi && typeof stateApi.getSelection === 'function'
        ? stateApi.getSelection()
        : null;
  const tz = String(/** @type {any} */ (sel)?.timezone || '').trim();
  return tz || null;
}

/**
 * Whether the MF36 inventory-enforcement window is currently active in the
 * bakery's local time. This only decides Today pickup for a bakery that has NOT
 * entered inventory (MF36 `bakeryStockSynced` not true): active → sold out,
 * inactive → purchasable with no hard limit. A synced bakery's MF20 stock is
 * enforced at any hour, so the window is never consulted for it.
 *
 * Bounds come from MO08 via `getInventoryWindowMinutes` (default 11:00–21:00).
 * Both are inclusive, and a window whose end precedes its start spans midnight.
 *
 * Timezone resolution: explicit `timezone` param → the selection's stored
 * bakery timezone → the browser's local time (stale pre-timezone selection
 * rows; the shopper's device is usually near their bakery).
 *
 * @param {string | null} [timezone] - IANA timezone (defaults from the selection)
 * @param {Date} [now] - injection point for tests
 * @returns {boolean}
 */
export function isInventoryWindowActive(timezone, now) {
  const tz = String(timezone ?? getSelectedBakeryTimezone() ?? '').trim();
  const at = now instanceof Date ? now : new Date();

  let hours = at.getHours();
  let minutes = at.getMinutes();

  if (tz) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const parts = formatter.formatToParts(at);
      const get = (/** @type {string} */ type) =>
        Number(parts.find((p) => p.type === type)?.value ?? NaN);
      const h = get('hour');
      const m = get('minute');
      if (Number.isFinite(h) && Number.isFinite(m)) {
        // `hour12: false` can yield "24" for midnight in some engines.
        hours = h === 24 ? 0 : h;
        minutes = m;
      }
    } catch {
      // Invalid timezone string — fall back to browser-local time.
    }
  }

  const total = hours * 60 + minutes;
  const { start, end } = getInventoryWindowMinutes();

  // A window that wraps midnight (end before start, e.g. 20:00–02:00) is two
  // ranges either side of 00:00, so the comparison flips to OR.
  return start <= end ? total >= start && total <= end : total >= start || total <= end;
}

/**
 * Determines the storefront availability state from MF16 / MF20 metafield data.
 *
 * Logic (kept in lock-step with snippets/availability-state.liquid and the
 * Hydrogen `Product.computeState`):
 *   1. If `bakeryId` (MF01) missing / blank                     → unavailable (badge: not at this store / no store)
 *   2. If MF16 list missing / empty, or `bakeryId` not in MF16  → unavailable (MF16 gate)
 *   3. If `pickupDay === 'tomorrow'` and `bakeryId` in MF16:
 *        a. MF34 `preorderAllowed === false`                    → unavailable (no preorders for this product)
 *        b. otherwise                                           → available (MF20 ignored; no “sold out for tomorrow”)
 *   4. Today pickup — MF36 inventory-synced gate. The bakery has NOT entered
 *      inventory today (`bakeryStockSynced[B] !== true`):
 *        a. MO08 window active (local bakery time, default 11:00am–9:00pm)
 *                                                               → sold_out (MF20 is NOT consulted — an unsynced
 *                                                                  bakery is blocked whatever MF20 holds)
 *        b. window inactive                                     → available (no hard limit)
 *   5. Today pickup, bakery IS synced — MF20 is enforced at ANY hour:
 *        a. stock missing or <= 0                               → sold_out
 *        b. otherwise                                           → available (capped at stock)
 *
 * `mf16Ids` entries and `bakeryId` are compared as trimmed strings (internal
 * bakery ids). `bakeryStock` / `bakeryStockSynced` keys are string lookups
 * using the same `B`.
 *
 * @param {string[] | null | undefined} mf16Ids   - availableAtBakeryIds (internal ids)
 * @param {Record<string, number> | null | undefined} mf20Stock - bakeryStock map
 * @param {string | number | null | undefined} bakeryId - MF01 internal id (or legacy normalised id)
 * @param {AvailabilityPickupDay} [pickupDay] - defaults from `getSelectedPickupDay()` when omitted
 * @param {boolean | null} [preorderAllowed] - MF34 `custom.mf34_product_allowed_preorder`.
 *   ONLY explicit `false` gates anything (true / null / undefined = no change),
 *   and only when `pickupDay === 'tomorrow'` — today pickup is never a preorder.
 * @param {Record<string, boolean> | null} [stockSynced] - MF36
 *   `custom.mf36_product_inventory_synced` bakeryStockSynced map. An explicit
 *   `true` for the selected bakery makes MF20 authoritative at any hour; false /
 *   missing key / missing metafield = inventory not entered today, and the MO08
 *   window then decides between sold_out (inside) and no hard limit (outside).
 * @param {boolean} [inventoryWindowActive] - whether the MO08 enforcement
 *   window (local bakery time, default 11:00am–9:00pm) is active. Only consulted
 *   for an unsynced bakery. Defaults from `isInventoryWindowActive()` (selection
 *   timezone) when omitted.
 * @returns {AvailabilityResult}
 */
export function getAvailabilityState(
  mf16Ids,
  mf20Stock,
  bakeryId,
  pickupDay,
  preorderAllowed,
  stockSynced,
  inventoryWindowActive
) {
  const day = pickupDay ?? getSelectedPickupDay();
  const B = normaliseAvailabilityBakeryId(bakeryId);
  if (!B) return { state: 'unavailable', stock: 0 };
  if (!Array.isArray(mf16Ids) || mf16Ids.length === 0) return { state: 'unavailable', stock: 0 };

  const locKeys = mf16Ids.map((id) => String(id).trim()).filter(Boolean);
  const inLocations = locKeys.some((id) => id === B);

  if (!inLocations) return { state: 'unavailable', stock: 0 };

  if (day === 'tomorrow') {
    if (preorderAllowed === false) return { state: 'unavailable', stock: 0 };
    return { state: 'available', stock: SHOPIFY_MAX_INVENTORY_QUANTITY };
  }

  // MF36 inventory-synced gate (Today pickup). A bakery that hasn't entered
  // inventory for the day has no stock figure worth trusting, so the MO08
  // local-bakery-time window decides: inside it the product is sold out, outside
  // it (early morning / late evening) it's purchasable with no hard limit —
  // franchisees enter production at inconsistent times and must not lose the
  // sale. MF20 is deliberately NOT consulted here; it can still hold the
  // previous day's figure until Boomi writes the new one.
  if (!isBakeryStockSynced(stockSynced, B)) {
    const windowActive = inventoryWindowActive ?? isInventoryWindowActive();
    return windowActive
      ? { state: 'sold_out', stock: 0 }
      : { state: 'available', stock: SHOPIFY_MAX_INVENTORY_QUANTITY };
  }

  // Synced: the bakery has declared today's stock, so MF20 is authoritative at
  // any hour — the window plays no part from here.

  let stockVal = mf20Stock?.[B];
  if (stockVal === undefined && mf20Stock && typeof mf20Stock === 'object') {
    const n = Number(B);
    if (!Number.isNaN(n)) {
      const alt = String(n);
      if (alt !== B) stockVal = mf20Stock[alt];
    }
  }
  const stockNum = Number(stockVal);

  if (stockVal == null || stockNum <= 0) return { state: 'sold_out', stock: 0 };

  return { state: 'available', stock: stockNum };
}

/**
 * MF36 lookup for the normalised bakery id `B`. Only an explicit `true` counts
 * as "this bakery has entered inventory today", which is what makes MF20
 * authoritative. Same numeric-key fallback as the MF20 lookup (keys may be
 * serialised with/without leading zeros).
 *
 * @param {Record<string, boolean> | null | undefined} stockSynced
 * @param {string} B - normalised bakery id
 * @returns {boolean}
 */
function isBakeryStockSynced(stockSynced, B) {
  if (!stockSynced || typeof stockSynced !== 'object') return false;
  let val = stockSynced[B];
  if (val === undefined) {
    const n = Number(B);
    if (!Number.isNaN(n)) {
      const alt = String(n);
      if (alt !== B) val = stockSynced[alt];
    }
  }
  return val === true;
}

/**
 * Convenience: returns true only when state === 'available'.
 *
 * @param {string[] | null | undefined} mf16Ids
 * @param {Record<string, number> | null | undefined} mf20Stock
 * @param {string | number | null | undefined} bakeryId
 * @param {AvailabilityPickupDay} [pickupDay]
 * @param {boolean | null} [preorderAllowed] - MF34; see `getAvailabilityState`
 * @param {Record<string, boolean> | null} [stockSynced] - MF36; see `getAvailabilityState`
 * @param {boolean} [inventoryWindowActive]
 * @returns {boolean}
 */
export function isAvailable(
  mf16Ids,
  mf20Stock,
  bakeryId,
  pickupDay,
  preorderAllowed,
  stockSynced,
  inventoryWindowActive
) {
  return (
    getAvailabilityState(
      mf16Ids,
      mf20Stock,
      bakeryId,
      pickupDay,
      preorderAllowed,
      stockSynced,
      inventoryWindowActive
    ).state === 'available'
  );
}

/**
 * Max units sellable at the selected bakery from MF20, only when storefront state is `available`.
 * Otherwise 0 (sold_out / unavailable / missing MF01). When the MF36 gate
 * bypasses inventory (bakery not synced AND the MO08 window inactive), this
 * returns the synthetic max — i.e. no hard limit. An unsynced bakery INSIDE the
 * window is `sold_out`, so the cap is 0.
 *
 * @param {string[] | null | undefined} mf16Ids
 * @param {Record<string, number> | null | undefined} mf20Stock
 * @param {string | number | null | undefined} bakeryId
 * @param {AvailabilityPickupDay} [pickupDay]
 * @param {boolean | null} [preorderAllowed] - MF34; see `getAvailabilityState`
 * @param {Record<string, boolean> | null} [stockSynced] - MF36; see `getAvailabilityState`
 * @param {boolean} [inventoryWindowActive]
 * @returns {number}
 */
export function getMf20StorefrontCap(
  mf16Ids,
  mf20Stock,
  bakeryId,
  pickupDay,
  preorderAllowed,
  stockSynced,
  inventoryWindowActive
) {
  const { state, stock } = getAvailabilityState(
    mf16Ids,
    mf20Stock,
    bakeryId,
    pickupDay,
    preorderAllowed,
    stockSynced,
    inventoryWindowActive
  );
  if (state !== 'available') return 0;
  const n = Math.floor(Number(stock));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Parse comma-separated internal bakery ids from data-availability-mf16.
 *
 * @param {string | null | undefined} raw
 * @returns {string[]}
 */
export function parseMf16Ids(raw) {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => String(s).trim())
    .filter((s) => s.length > 0);
}

/**
 * Parse the bakeryStock JSON from a data-availability-mf20 attribute.
 *
 * @param {string | null | undefined} raw
 * @returns {Record<string, number> | null}
 */
export function parseMf20Stock(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Parse the MF36 (`custom.mf36_product_inventory_synced`) bakeryStockSynced
 * map from a data-availability-mf36 attribute. Only entries that are
 * explicitly boolean `true` are kept truthy — anything else (false, strings,
 * numbers) is normalised to `false`, matching the "only explicit true means the
 * bakery has entered inventory today" contract.
 *
 * @param {string | null | undefined} raw
 * @returns {Record<string, boolean> | null}
 */
export function parseMf36StockSynced(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    /** @type {Record<string, boolean>} */
    const out = {};
    for (const [key, val] of Object.entries(parsed)) {
      const k = String(key).trim();
      if (k) out[k] = val === true;
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Parse MF34 (`custom.mf34_product_allowed_preorder`) from a
 * data-availability-mf34 attribute. Liquid renders the boolean metafield's
 * `.value` verbatim: `"true"`, `"false"`, or `""` when unset. Only the
 * explicit strings map to booleans — anything else is `null` (no gate),
 * matching the "MF34 true and empty have no change" contract.
 *
 * @param {string | null | undefined} raw
 * @returns {boolean | null}
 */
export function parseMf34PreorderAllowed(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'false') return false;
  if (s === 'true') return true;
  return null;
}

/**
 * @returns {string | null}
 */
function getSelectedBakeryId() {
  return getSelectedAvailabilityBakeryId();
}

/**
 * Per-element override for the SSD `pickupDay` value used by state computation.
 *
 * The recommendations carousel (`<product-recommendations>` in
 * blocks/sections/product-recommendations.liquid) stamps
 * `data-availability-pickup-day-override="today"` on its root so that MF20 is
 * STRICTLY enforced on those cards regardless of the shopper's SSD selection
 * — a `+` quick-add on a zero-stock product in a discovery carousel is
 * misleading. Other surfaces (PLP grid, PDP, cart) don't stamp this attribute
 * and therefore keep the documented tomorrow-bypass behaviour.
 *
 * @param {HTMLElement} el
 * @param {AvailabilityPickupDay} fallback
 * @returns {AvailabilityPickupDay}
 */
function resolveEffectivePickupDay(el, fallback) {
  const host = /** @type {HTMLElement | null} */ (
    el.closest('[data-availability-pickup-day-override]')
  );
  if (!host) return fallback;
  const raw = String(host.dataset.availabilityPickupDayOverride || '').trim().toLowerCase();
  return raw === 'tomorrow' ? 'tomorrow' : 'today';
}

/**
 * Effective card availability — the 3 storefront states plus `preorder_blocked`
 * (MF34-false + Tomorrow pickup). `preorder_blocked` is a FIRST-CLASS card state
 * (`card.dataset.availability="preorder_blocked"`) so the PLP can keep its price +
 * "Same-day Only" badge visible while still hiding quick-add — distinct from a
 * true MF16 miss (`unavailable`), whose price/badge are hidden on the grid.
 *
 * @typedef {AvailabilityState | 'preorder_blocked'} EffectiveAvailabilityState
 */

/**
 * @param {HTMLElement} el
 * @param {string | null} bakeryId
 * @param {AvailabilityPickupDay} pickupDay
 */
function updateElement(el, bakeryId, pickupDay) {
  const mf16Ids = parseMf16Ids(el.dataset.availabilityMf16);
  const mf20Stock = parseMf20Stock(el.dataset.availabilityMf20);
  const preorderAllowed = parseMf34PreorderAllowed(el.dataset.availabilityMf34);
  const stockSynced = parseMf36StockSynced(el.dataset.availabilityMf36);
  const effectivePickupDay = resolveEffectivePickupDay(el, pickupDay);
  const { state } = getAvailabilityState(
    mf16Ids,
    mf20Stock,
    bakeryId,
    effectivePickupDay,
    preorderAllowed,
    stockSynced
  );

  // MF34 is the BLOCKING reason only when the product would otherwise be
  // purchasable for tomorrow — pill reads "Same-day Only" instead of the
  // generic "Unavailable". MF16 misses keep the standard label.
  const preorderBlocked =
    state === 'unavailable' &&
    preorderAllowed === false &&
    effectivePickupDay === 'tomorrow' &&
    getAvailabilityState(mf16Ids, mf20Stock, bakeryId, effectivePickupDay, null, stockSynced)
      .state === 'available';

  // With no bakery chosen we can't know whether a product is carried, so the
  // pill would claim "Unavailable" for every product on the page. Mirror the
  // server render (snippets/availability-state.liquid leaves the badge hidden
  // when `selected_bakery_id` is blank) and show nothing. The card is still
  // stamped `unavailable` so quick-add stays suppressed — there's no bakery to
  // add against.
  renderAvailability(el, preorderBlocked ? 'preorder_blocked' : state, !bakeryId);
}

/**
 * Render a badge element + its owning card for an already-decided effective
 * state. Shared by the client-compute path (`updateElement`) and the
 * server-authoritative path (`applyServerAvailabilityStates`), so both produce
 * identical DOM.
 *
 * @param {HTMLElement} el
 * @param {EffectiveAvailabilityState} effState
 * @param {boolean} [hidePill] - render the state (card dataset, quick-add) but
 *   keep the pill itself hidden, e.g. when no bakery is selected.
 */
function renderAvailability(el, effState, hidePill) {
  el.dataset.availabilityState = effState;

  // Stamped so availability-grid-sort.js (and the sold-out/miss CSS) can tell
  // "Same-day Only" cards apart from plain unavailable / sold-out ones.
  el.dataset.availabilityPreorderBlocked = effState === 'preorder_blocked' ? 'true' : 'false';

  const suppressBadge = hidePill === true || el.dataset.availabilitySuppressBadge === 'true';

  if (suppressBadge || effState === 'available') {
    el.hidden = true;
    el.textContent = '';
  } else {
    const label =
      effState === 'sold_out'
        ? (el.dataset.labelSoldOut ?? 'Sold out today')
        : effState === 'preorder_blocked'
          ? (el.dataset.labelPreorderBlocked ?? 'Same-day Only')
          : (el.dataset.labelUnavailable ?? 'Unavailable');
    el.textContent = label;
    el.hidden = false;
  }

  updateQuickAdd(el, effState);
}

/**
 * Applies a server-authoritative availability state map (from the Hydrogen
 * availability-order endpoint) to every badge under `root`, so the PLP renders
 * badges / price-hiding / quick-add from the server's classification rather than
 * recomputing MF16/MF20/MF34 on the client. Cards not present in the map are
 * left untouched.
 *
 * @param {HTMLElement} root - the `<results-list>` whose grid was reordered
 * @param {Record<string, EffectiveAvailabilityState>} statesMap - productId → state
 */
export function applyServerAvailabilityStates(root, statesMap) {
  if (!root || !statesMap) return;
  const badges = root.querySelectorAll('[data-availability-state]');
  for (const el of badges) {
    const idEl = /** @type {HTMLElement | null} */ (el.closest('[data-product-id]'));
    const id = idEl ? String(idEl.dataset.productId || '').trim() : '';
    if (!id) continue;
    const state = statesMap[id];
    if (!state) continue;
    renderAvailability(/** @type {HTMLElement} */ (el), state);
  }
}

/**
 * @param {HTMLElement} el
 * @param {EffectiveAvailabilityState} state
 */
function updateQuickAdd(el, state) {
  const card = /** @type {HTMLElement} */ (el.closest('product-card, product-tile'));
  if (!card) return;

  if (state === 'available') {
    delete card.dataset.availability;
  } else {
    card.dataset.availability = state;
  }

  const contentClass = card.tagName.toLowerCase() === 'product-tile'
    ? '.product-tile__content'
    : '.product-card__content';

  const contentDiv = card.querySelector(contentClass);
  if (!(contentDiv instanceof HTMLElement)) return;

  const quickAddEnabled = card.dataset.quickAddEnabled === 'true';
  const mobileEnabled = card.dataset.quickAddMobileEnabled === 'true';

  if (state === 'available' && quickAddEnabled) {
    contentDiv.style.setProperty('--quick-add-display', 'flex');
  } else {
    contentDiv.style.setProperty('--quick-add-display', 'none');
  }

  if (state === 'available' && mobileEnabled) {
    contentDiv.style.setProperty('--quick-add-mobile-display', 'flex');
  } else {
    contentDiv.style.setProperty('--quick-add-mobile-display', 'none');
  }
}

/**
 * Called on DOMContentLoaded and on every store-selection:changed event.
 */
export function hydrateAvailabilityElements() {
  const bakeryId = getSelectedBakeryId();
  const pickupDay = getSelectedPickupDay();

  const elements = document.querySelectorAll('[data-availability-state]');

  for (const el of elements) {
    // Collection cards under a server-managed <results-list> are owned by the
    // Hydrogen availability-order response (applied in availability-grid-sort.js).
    // Skip client recomputation so the two never disagree — e.g. an MF34
    // "Same-day Only" product would otherwise be reclassified here.
    if (el.closest('results-list[data-availability-server-managed]')) continue;
    updateElement(/** @type {HTMLElement} */ (el), bakeryId, pickupDay);
  }
}

document.addEventListener(ThemeEvents.PageContentAppended, () => {
  hydrateAvailabilityElements();
});

/**
 * Binds store listeners and runs an initial hydrate. Must run even when this
 * module loads after `DOMContentLoaded` (typical for `type="module"` tags deep
 * in the document, e.g. first `availability-state` inside recommendation cards).
 */
function bindAvailabilityHydration() {
  hydrateAvailabilityElements();

  const stateApi = /** @type {any} */ (window).StoreSelectionState;

  if (stateApi && typeof stateApi.subscribe === 'function') {
    stateApi.subscribe(() => hydrateAvailabilityElements());
  } else {
    window.addEventListener('store-selection:changed', () => hydrateAvailabilityElements());
    window.addEventListener('store-selection:cleared', () => hydrateAvailabilityElements());
  }

  window.addEventListener(
    'load',
    () => {
      hydrateAvailabilityElements();
    },
    { once: true }
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindAvailabilityHydration, { once: true });
} else {
  queueMicrotask(() => bindAvailabilityHydration());
}
